import { CalculatedBuffer, ProjectedBuffer } from "@nova/calculate-geospatial"
import { DistanceUnit, BufferError } from "@nova/constants"
import { BufferRange, PerimeterBuffer, Polygon } from "@nova/protobuf"
import proj4 from "proj4"
import { runTask } from "@nova/webworker-pool"
import { createBuffer } from "./create-buffer-task"

export { createBuffer }

/**
 * Project [longitude, latitude] to Web Mercator [x, y] meters
 */
const projectCoordinateToXYMeters = (coord: [number, number]): [number, number] => {
    return proj4("EPSG:4326", "EPSG:3857", coord)
}

/**
 * Project Web Mercator [x, y] meters to [longitude, latitude]
 */
const projectXYMetersToCoordinate = (coord: [number, number]): [number, number] => {
    return proj4("EPSG:3857", "EPSG:4326", coord)
}

/**
 * Calculate the latitude correction factor to adjust buffer distances for Web Mercator distortion.
 * Web Mercator stretches distances by 1/cos(latitude), so we apply this correction to ensure
 * accurate real-world meter measurements regardless of latitude.
 */
const calculateLatitudeCorrectionFactor = (latitude: number): number => {
    const latitudeInRadians = (latitude * Math.PI) / 180
    return 1 / Math.cos(latitudeInRadians)
}

const buffersByDistanceByPolygon = new WeakMap<WeakKey, Record<number, ProjectedBuffer[]>>()

/**
 * Calls generateBufferLinesAsync. Throws error if buffer distance is too large or buffer
 * generation fails.
 */
export const testNewBufferDistance = async (
    polygons: Polygon[],
    range: BufferRange,
    unit: DistanceUnit
) => {
    return await generateBufferLinesAsync(polygons, { range: [range], unit }, (error) => {
        throw error
    })
}

export const generateBufferLinesAsync = async (
    polygons: Polygon[],
    buffers: PerimeterBuffer,
    onError?: (error: BufferError) => void,
    rootAbortSignal?: AbortSignal
): Promise<CalculatedBuffer[][]> => {
    const abortControllers: AbortController[] = []
    rootAbortSignal?.addEventListener("abort", () => {
        abortControllers.forEach((controller) => controller.abort())
    })

    const bufferedPolygons: CalculatedBuffer[][] = await Promise.all(
        polygons.map(async (polygon) => {
            // Calculate the average latitude for this polygon to determine correction factor
            const averageLatitude =
                polygon.coordinates.reduce((sum, coord) => sum + coord.latitude, 0) /
                polygon.coordinates.length
            const correctionFactor = calculateLatitudeCorrectionFactor(averageLatitude)

            // Project coordinates to Web Mercator [x, y] meters
            const projectedCoordinates: [number, number][] = polygon.coordinates.map(
                ({ longitude, latitude }) => projectCoordinateToXYMeters([longitude, latitude])
            )

            const { range, unit } = buffers

            const projectedBufferCreationTasks = await Promise.allSettled<ProjectedBuffer[]>(
                range.map(async (range) => {
                    try {
                        const cachedBuffersByDistance = buffersByDistanceByPolygon.get(polygon)
                        const cachedBuffer = cachedBuffersByDistance?.[range.distance]
                        if (cachedBuffer) {
                            return cachedBuffer.map((buffer) => ({
                                ...buffer,
                                color: range.color,
                                width: range.width,
                            }))
                        }

                        let abortController: AbortController | undefined
                        if (rootAbortSignal) {
                            abortController = new AbortController()
                            abortControllers.push(abortController)
                        }

                        // Buffer distance is now directly in meters (negative for inward buffer).
                        // Apply latitude correction to get accurate real-world meters in Web Mercator.
                        const correctedDistance = -range.distance * correctionFactor
                        const computedBufferXYPairs = await createBufferAsync(
                            projectedCoordinates,
                            correctedDistance,
                            abortController?.signal
                        )

                        const projectedBuffer = computedBufferXYPairs.map((polygon) => {
                            // Deduplicate points using centimeter precision.

                            const foundPairs: Record<string, [number, number]> = {}
                            polygon.forEach((position) => {
                                const key = `${Math.round(position[0] * 100)},${Math.round(position[1] * 100)}`
                                foundPairs[key] ??= position
                            })

                            return {
                                ...range,
                                unit: unit as DistanceUnit,
                                coordinates: Object.values(foundPairs),
                            } satisfies ProjectedBuffer
                        })

                        // Cache the buffer polygons at this range.
                        buffersByDistanceByPolygon.set(polygon, {
                            ...buffersByDistanceByPolygon.get(polygon),
                            [range.distance]: projectedBuffer,
                        })

                        return projectedBuffer
                    } catch (error) {
                        const castError = error as BufferError
                        console.error(
                            `Failed to create perimeter buffer at distance ${range.distance} meters.`,
                            {
                                reason:
                                    castError === BufferError.DistanceTooLarge
                                        ? `Buffer distance of ${range.distance} meters is too large.`
                                        : "unknown",
                            },
                            error
                        )

                        onError?.(castError)
                        throw error
                    }
                })
            )

            const projectedBuffersForThisPolygon = projectedBufferCreationTasks
                .filter((result) => result.status === "fulfilled")
                .flatMap(({ value }) => value)

            // Convert back from Web Mercator meters to lat/lng
            const buffersForThisPolygon: CalculatedBuffer[] = projectedBuffersForThisPolygon.map(
                (buffer) => ({
                    ...buffer,
                    coordinates: buffer.coordinates.map((xyPair) => {
                        const [longitude, latitude] = projectXYMetersToCoordinate(xyPair)
                        return { longitude, latitude }
                    }),
                })
            )

            return buffersForThisPolygon
        })
    )

    return bufferedPolygons
}

/**
 * Returns an array of polygons that make up the buffer for a specified distance in meters.
 */
const createBufferAsync = async (
    coordinates: [number, number][],
    distanceMeters: number,
    abortSignal?: AbortSignal
): Promise<[number, number][][]> => {
    // Remove duplicate consecutive points (using 0.01m tolerance for uniqueness)
    const keys = new Set<string>()
    const uniqueCoordinates = coordinates.reduce(
        (uniqueCoordinates, coordinate) => {
            // Round to centimeter precision for deduplication key
            const key = `${Math.round(coordinate[0] * 100)},${Math.round(coordinate[1] * 100)}`
            if (!keys.has(key)) {
                keys.add(key)
                uniqueCoordinates.push(coordinate)
            }
            return uniqueCoordinates
        },
        [] as [number, number][]
    )

    // Ensure polygon is closed
    const first = uniqueCoordinates[0]
    const last = uniqueCoordinates[uniqueCoordinates.length - 1]
    if (first[0] !== last[0] || first[1] !== last[1]) {
        uniqueCoordinates.push([...first])
    }

    return await runTask(
        createBuffer,
        { xyCoordinates: uniqueCoordinates, bufferSizeInPixels: distanceMeters },
        { signal: abortSignal }
    )
}
